import React from 'react'
import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline'
import { SyncOrdersResponse } from '../../types/homecenter'

interface SyncResultsModalProps {
  isOpen: boolean
  onClose: () => void
  results: SyncOrdersResponse | null
  isLoading?: boolean
  syncType: 'all' | 'dateRange'
  dateRange?: { startDate: string; endDate: string }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface DayResult {
  dayLabel: string
  success: boolean
  results: {
    total: number
    created: number
    skipped: number
    errors: number
  }
  error?: string
}

const SyncResultsModal: React.FC<SyncResultsModalProps> = ({
  isOpen,
  onClose,
  results,
  isLoading = false,
  syncType,
  dateRange
}) => {
  if (!isOpen) return null

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getStatusIcon = (success: boolean) => {
    return success ? (
      <CheckCircleIcon className="h-5 w-5 text-green-500" />
    ) : (
      <XCircleIcon className="h-5 w-5 text-red-500" />
    )
  }

  const getStatusColor = (success: boolean) => {
    return success ? 'text-green-600' : 'text-red-600'
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div className="relative bg-white rounded-lg shadow-xl p-6 m-4 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-custom-blue">
            Resultados de Sincronización
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <ClockIcon className="h-16 w-16 text-blue-500 mx-auto mb-4 animate-spin" />
            <p className="text-lg text-gray-600">Sincronizando pedidos...</p>
          </div>
        ) : results ? (
          <div className="space-y-6">
            {/* Información general */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-4">
                {getStatusIcon(results.success)}
                <div>
                  <h3 className={`text-lg font-semibold ${getStatusColor(results.success)}`}>
                    {results.success ? 'Sincronización Exitosa' : 'Error en Sincronización'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {syncType === 'all' 
                      ? 'Sincronización de todos los pedidos completada'
                      : `Sincronización del ${formatDate(dateRange?.startDate || '')} al ${formatDate(dateRange?.endDate || '')} completada`
                    }
                  </p>
                </div>
              </div>
              <p className="text-gray-700">{results.message}</p>
            </div>

            {/* Resumen de resultados */}
            {results.results && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-2xl font-bold text-blue-600">
                    {results.results.total}
                  </div>
                  <div className="text-sm text-blue-700">Total Procesados</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-2xl font-bold text-green-600">
                    {results.results.created}
                  </div>
                  <div className="text-sm text-green-700">Creados</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="text-2xl font-bold text-yellow-600">
                    {results.results.skipped}
                  </div>
                  <div className="text-sm text-yellow-700">Omitidos</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="text-2xl font-bold text-red-600">
                    {results.results.errors}
                  </div>
                  <div className="text-sm text-red-700">Errores</div>
                </div>
              </div>
            )}

            {/* Detalles por día (si es sincronización por fechas) */}
            {syncType === 'dateRange' && dateRange && (
              <div className="mt-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                  Detalles por Día
                </h4>
                <div className="space-y-3">
                  {/* Aquí se mostrarían los resultados día por día si los tuviéramos */}
                  <div className="text-sm text-gray-600 italic">
                    Los detalles por día se mostrarían aquí cuando estén disponibles
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-600">No hay resultados para mostrar</p>
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-custom-blue text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

export default SyncResultsModal
